// ── companyAccountDb.js ───────────────────────────────────────────────────────
// Company account system data layer. Uses existing db.js schema.
// Each function takes `supabase` as first param.

export const PLAN_SEATS = {
  professional: 3,
  business:     999,
  // Legacy aliases for backward compat
  starter:      1,
  pro:          3,
  team:         999,
  agency:       999,
};

export function getSeatLimit(plan) {
  return PLAN_SEATS[plan] || 1;
}

// ── Company ───────────────────────────────────────────────────────────────────

/**
 * Returns { company, role } for the user — checking ownership first, then membership.
 * Returns null if the user has no company.
 */
export async function getMyCompany(supabase, userId) {
  // Check ownership
  const { data: owned } = await supabase
    .from('companies')
    .select('*, company_members(id, user_id, role, status, invite_email, invite_token, invite_sent_at, invite_accepted_at, invited_email, invite_accepted)')
    .eq('owner_id', userId)
    .maybeSingle();
  if (owned) return { company: owned, role: 'owner' };

  // Check active membership
  const { data: membership } = await supabase
    .from('company_members')
    .select('*, company:company_id(*, company_members(id, user_id, role, status, invited_email, invite_accepted))')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();
  if (membership) return { company: membership.company, role: membership.role };

  return null;
}

export async function createCompany(supabase, userId, data) {
  // Normalize: spec uses company_name, schema uses name
  const { company_name, ...rest } = data;
  const { data: company, error } = await supabase
    .from('companies')
    .insert({ owner_id: userId, name: company_name || '', ...rest })
    .select()
    .single();
  if (error) { console.error('createCompany:', error); return null; }
  return company;
}

export async function saveCompany(supabase, companyId, updates) {
  // Normalize company_name → name
  const { company_name, ...rest } = updates;
  const payload = { ...rest, updated_at: new Date().toISOString() };
  if (company_name !== undefined) payload.name = company_name;
  const { data, error } = await supabase
    .from('companies')
    .update(payload)
    .eq('id', companyId)
    .select()
    .single();
  if (error) { console.error('saveCompany:', error); return null; }
  return data;
}

// ── Members ───────────────────────────────────────────────────────────────────

/**
 * Invite a new member. Returns the invite record with invite_link attached.
 * Enforces seat limits before inserting.
 */
export async function inviteMember(supabase, companyId, invitedBy, email, role = 'member', ownerPlan = 'professional') {
  // Check seats
  const usage = await getSeatUsage(supabase, companyId, ownerPlan);
  if (usage.used >= usage.limit) {
    return { error: `Seat limit reached (${usage.used}/${usage.limit} on ${ownerPlan} plan). Upgrade to add more members.` };
  }

  // Duplicate check
  const { data: existing } = await supabase
    .from('company_invites')
    .select('id')
    .eq('company_id', companyId)
    .eq('email', email.toLowerCase().trim())
    .is('accepted_at', null)
    .maybeSingle();
  if (existing) {
    return { error: 'This email already has a pending invite.' };
  }

  const { data, error } = await supabase
    .from('company_invites')
    .insert({
      company_id:  companyId,
      email:       email.toLowerCase().trim(),
      role:        role,
      invited_by:  invitedBy,
      expires_at:  new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    })
    .select()
    .single();
  if (error) { console.error('inviteMember:', error); return { error: 'Failed to create invite.' }; }
  return { ...data, invite_link: `${window.location.origin}?company_invite=${data.token}` };
}

export async function acceptCompanyInviteChecked(supabase, token, userId, userPlan) {
  const { data: invite, error: findErr } = await supabase
    .from('company_invites')
    .select('*, companies(id, name, owner_id, plan)')
    .eq('token', token)
    .is('accepted_at', null)
    .maybeSingle();

  if (findErr || !invite) return { success: false, error: 'Invalid or expired invite link.' };
  if (new Date(invite.expires_at) < new Date()) return { success: false, error: 'This invite has expired. Contact your company admin.' };

  // Fetch owner plan for seat check
  const { data: ownerProfile } = await supabase
    .from('profiles')
    .select('plan')
    .eq('id', invite.companies.owner_id)
    .single();

  const ownerPlanStr = ownerProfile?.plan || 'professional';
  const seatLimit = getSeatLimit(ownerPlanStr);

  const { count } = await supabase
    .from('company_members')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', invite.company_id)
    .eq('status', 'active');

  if ((count || 0) >= seatLimit) {
    return {
      success: false,
      error: `This company has reached its seat limit (${seatLimit} seats on the ${ownerPlanStr} plan). The owner must upgrade to add more members.`,
    };
  }

  // Accept
  await supabase
    .from('company_invites')
    .update({ accepted_at: new Date().toISOString() })
    .eq('token', token);

  await supabase
    .from('company_members')
    .upsert({
      company_id:          invite.company_id,
      user_id:             userId,
      invited_email:       invite.email,
      role:                invite.role,
      invite_accepted:     true,
      status:              'active',
      invite_accepted_at:  new Date().toISOString(),
      invited_by:          invite.invited_by,
    }, { onConflict: 'company_id,user_id' });

  return {
    success: true,
    companyName: invite.companies?.name || 'your team',
    companyId:   invite.company_id,
    role:        invite.role,
  };
}

export async function removeMemberById(supabase, memberId) {
  const { error } = await supabase
    .from('company_members')
    .update({ status: 'removed' })
    .eq('id', memberId);
  return !error;
}

export async function changeMemberRole(supabase, memberId, newRole) {
  const { data, error } = await supabase
    .from('company_members')
    .update({ role: newRole })
    .eq('id', memberId)
    .select()
    .single();
  if (error) { console.error('changeMemberRole:', error); return null; }
  return data;
}

// ── Seat usage ────────────────────────────────────────────────────────────────

export async function getSeatUsage(supabase, companyId, ownerPlan) {
  const { count, error } = await supabase
    .from('company_members')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .eq('status', 'active');

  if (error) console.error('getSeatUsage:', error);
  const used  = count || 0;
  const limit = getSeatLimit(ownerPlan);
  return { used, limit, remaining: Math.max(0, limit - used), pct: Math.round((used / limit) * 100) };
}
